// CardGrid.jsx — SECONDO componente condiviso del design system (F2.3g).
// Griglia di CARD responsive con gli STATI OBBLIGATORI del gestionale
// (regole UI 4, stessa filosofia del DataTable):
//   loading  → spinner + 'Caricamento…' (mai schermo che finge)
//   error    → messaggio + bottone 'Riprova' (callback onRetry del chiamante)
//   empty    → messaggio DEDICATO (emptyMessage), mai griglia vuota muta
//   data     → card; con card presenti il reload in corso/fallito NON svuota
//              la griglia (precedenza in card-grid-core.selectCardState, che
//              RIUSA selectTableState di data-table-core — testata).
//
// API (pensata per riuso, mandato ondata 22):
//   items        array di oggetti card
//   itemKey      campo (stringa) o funzione item→chiave; fallback indice
//   renderCard   RENDER-PROP (item, index) → contenuto della card. Il CHROME
//                della card (fondo, bordo, raggio, padding) lo mette CardGrid
//                (.cg-card): il contenuto resta del consumatore.
//   onCardClick? (item) → card INTERA cliccabile (hover + cursor solo se
//                presente); il click IGNORA i controlli interattivi interni
//                (button/input/a/select/.cg-no-card — stessa guardia del
//                DataTable, logica in card-grid-core.isInteractiveTarget).
//   twoColAt?    'md' (default, 2 colonne da 768px) | 'lg' (da 1024px) |
//                null (sempre 1 colonna) — gli stessi breakpoint Tailwind
//                delle card-grid del monolite (Templates md, Monitoraggio lg).
//   loading, error (stringa|Error), onRetry?, emptyMessage
//
// Stile sui token --shell-* (tokens.css.js) con fallback campionati, come il
// DataTable. Primi consumatori reali: proc-v3 Monitoraggio e Templates.

import React from 'react';
import { selectCardState, cardKeyOf, isInteractiveTarget, gridClass } from './card-grid-core.js';

const STYLE_ID = 'shell-ui-cardgrid-style';

function css() {
  return [
    '.cg-grid{display:grid;grid-template-columns:1fr;gap:16px}',
    // 2 colonne dal breakpoint dichiarato (md/lg): minmax(0,1fr) evita che i
    // contenuti larghi (pre dei template) sfondino la colonna.
    '@media(min-width:768px){.cg-grid-md2{grid-template-columns:repeat(2,minmax(0,1fr))}}',
    '@media(min-width:1024px){.cg-grid-lg2{grid-template-columns:repeat(2,minmax(0,1fr))}}',
    '.cg-card{background:var(--shell-surface,#fff);border:1px solid #e2e8f0;border-radius:12px;box-shadow:0 1px 2px rgba(15,23,42,.05);padding:16px;min-width:0;color:var(--shell-text,#0f172a)}',
    '.cg-card.cg-click{cursor:pointer}',
    '.cg-card.cg-click:hover{background:#f8fafc}',
    // stati (stessa resa del design system: box dedicato, mai silenzio)
    '.cg-state{background:var(--shell-surface,#fff);border:1px solid #e2e8f0;border-radius:12px;padding:24px;text-align:center;color:#94a3b8;font-size:13px}',
    '.cg-loading{display:flex;align-items:center;justify-content:center;gap:10px}',
    '.cg-spin{width:18px;height:18px;border-radius:50%;border:2px solid #cffafe;border-top-color:var(--shell-primary,#06b6d4);animation:cg-rot .8s linear infinite;display:inline-block}',
    '@keyframes cg-rot{to{transform:rotate(360deg)}}',
    '.cg-error{border-color:#fecaca;background:#fef2f2}',
    '.cg-errmsg{color:#b91c1c;font-size:13px;font-weight:600}',
    '.cg-retry{margin-top:12px;background:var(--shell-primary-strong,#0e7490);color:#fff;border:0;border-radius:8px;padding:8px 16px;font-weight:600;cursor:pointer;font-size:13px}',
  ].join('\n');
}

export function injectCardGridStyle(doc) {
  if (!doc || doc.getElementById(STYLE_ID)) return;
  const st = doc.createElement('style');
  st.id = STYLE_ID;
  st.textContent = css();
  doc.head.appendChild(st);
}

export function CardGrid(props) {
  const {
    items, itemKey, renderCard, onCardClick,
    twoColAt = 'md', loading, error, onRetry, emptyMessage,
  } = props;
  const list = items || [];
  const wrapRef = React.useRef(null);

  // stile una volta per documento, dal documento REALE in cui il componente vive
  React.useEffect(() => {
    const el = wrapRef.current;
    if (el && el.ownerDocument) injectCardGridStyle(el.ownerDocument);
  }, []);

  const state = selectCardState({ loading, error, items: list });
  let body;
  if (state === 'loading') {
    body = (
      <div className="cg-state cg-loading" role="status">
        <span className="cg-spin" />
        <span>Caricamento…</span>
      </div>
    );
  } else if (state === 'error') {
    const msg = error && error.message ? error.message : String(error);
    body = (
      <div className="cg-state cg-error" role="alert">
        <div className="cg-errmsg">{'⚠ ' + msg}</div>
        {typeof onRetry === 'function' && (
          <button type="button" className="cg-retry" onClick={() => onRetry()}>Riprova</button>
        )}
      </div>
    );
  } else if (state === 'empty') {
    body = <div className="cg-state cg-empty">{emptyMessage || 'Nessun elemento.'}</div>;
  } else {
    body = (
      <div className={gridClass(twoColAt)}>
        {list.map((item, i) => (
          <div
            key={cardKeyOf(item, itemKey, i)}
            className={'cg-card' + (onCardClick ? ' cg-click' : '')}
            onClick={onCardClick ? (e) => { if (isInteractiveTarget(e.target)) return; onCardClick(item); } : undefined}
          >
            {typeof renderCard === 'function' ? renderCard(item, i) : null}
          </div>
        ))}
      </div>
    );
  }
  return <div className="cg-wrap" ref={wrapRef}>{body}</div>;
}
